import { useContext } from 'react';
import { ProgressBar } from '@launchpad-ui/components';

import { hasAnyPendingCommand } from '../utils/has-any-pending-command';

import { CommandPaletteDerivedQueryContext } from './command-palette-derived-query-context';
import { useCommandRegistry } from './command-registry-provider';

/**
 * Thin indicator that renders the indeterminate `<ProgressBar>` when
 * any registered command has `status === 'pending'`.
 *
 * Lives as its own component (rather than living inside the search
 * field) so that registry churn -- which fires every time any
 * consumer hook publishes new commands -- re-renders only the
 * indicator, NEVER the `<Input>` next to it. That's the difference
 * between a 1ms re-render of an empty div and a re-render of the
 * RAC TextField + Input + focus management plumbing.
 *
 * Lights up on EITHER signal:
 *
 *   - `isSearchSettling`: the user has typed but the debounce window
 *     hasn't fired yet. Shows the spinner instantly, bridging the gap
 *     before any fetch starts -- otherwise debouncing would leave a
 *     dead window where the input moved but nothing acknowledged it.
 *   - `hasAnyPendingCommand`: a consumer fetch is actually in flight.
 *
 * The handoff is seamless: settling stays true until the debounce
 * fires, at which point the fetch starts and flips a command to
 * `pending`, so the spinner never blinks off in between.
 */
export function CommandPalettePendingIndicator() {
  const { commands } = useCommandRegistry();
  const derived = useContext(CommandPaletteDerivedQueryContext);
  const isSearchSettling = derived?.isSearchSettling ?? false;
  if (!isSearchSettling && !hasAnyPendingCommand(commands)) {
    return null;
  }
  return <ProgressBar aria-label="loading" isIndeterminate />;
}
